import { Box, Typography, useTheme } from "@mui/material";
import { ForwardedRef, forwardRef, useState } from "react";
import { addCommas } from "@persian-tools/persian-tools";
import Boxoutline from "../../../../../media/images/icons/outline-box";

import CustomButton from "../../../../components/CustomButton";
import {
  convertToJalali,
  flattenObject,
  splitPelak,
} from "../../../../../utils/functions";
import CarTagView from "../../../../components/CarTag";
import ArrowTickLeft from "../../../../../media/images/icons/arrow-tick-left";
import { grey } from "@mui/material/colors";
import PaymentBottomSheet from "./PaymentBottomSheet";
import { IBolItem } from "../../../../../types/bolsHistories";

const DoneBolsCard = forwardRef(
  (props: IBolItem, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      goodsName,
      id,
      bolNr,
      src,
      dest,
      licensePlateNr,
      licensePlateSerial,
      senderAddress,
      receiverAddress,
      senderInfoFullName,
      receiverInfoFullName,
      driverInfoMobileNr,
      driverInfoNationalCode,
      driverFullName,
      goodsCost,
      netWeight,
      fare,
      bolDate,
      licenseNr,
    } = props;
    const theme = useTheme();

    const [open, setOpen] = useState(false);

    const [barDetailsBottomSheet, setPaymentBottomSheet] = useState(false);

    const { alpha, firstTowDigits, lastThiredDigits } =
      splitPelak(licensePlateNr);

    const handleDownload = async () => {
      const fileUrl = `https://update.bita24.com/ApplicationReports/KarenTarabarnet/${licenseNr}.pdf`;

      try {
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error("Failed to fetch file");
        }
        const blob = await response.blob();

        // Create a download link programmatically
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${licenseNr}.pdf`; // Set the file name
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error("Error downloading the file:", error);
      }
    };

    const getPdfTrigger = async (object: any) => {
      await fetch(`https://deyetspapi.bita24.com/api/Report/GetPdf`, {
        method: "POST",
        credentials: "omit",
        body: JSON.stringify(object),
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
          charset: "utf-8",
        },
      })
        .then((response) => response)
        .then((resp) => {
          handleDownload();
        })
        .catch((err) => {
          console.error(err);
          alert("در پردازش فایل خطایی به وجود آمده است" + err);
        });
    };

    const handlePrint = () => {
      const { id, ...rest } = props;

      getPdfTrigger({
        id: licenseNr,
        RepName: "KarenTarabarnet",
        ...flattenObject(rest),
      });
    };

    return (
      <>
        <PaymentBottomSheet
          open={barDetailsBottomSheet}
          onDismiss={() => setPaymentBottomSheet(false)}
          fare={fare}
          id={id}
        />
        <Box
          ref={ref}
          sx={{
            padding: "8px",
            borderColor: "bgColor.blueGrey100",
            borderWidth: "1px",
            borderStyle: "solid",
            borderRadius: "16px",
            backgroundColor: "bgColor.white",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: "8px",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  backgroundColor: theme.palette.bgColor.blueGrey100,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "6px",
                  borderRadius: "8px",
                }}
              >
                <Boxoutline color={theme.palette.textColor.main} />
              </Box>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: "700",
                      fontSize: "14px",
                    }}
                  >
                    شماره بارنامه:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                    }}
                  >
                    {bolNr}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: "700",
                      fontSize: "14px",
                    }}
                  >
                    تاریخ بارنامه:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                    }}
                  >
                    {convertToJalali(bolDate)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "700",
                    }}
                  >
                    نام محموله:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    {goodsName}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "700",
                    }}
                  >
                    کرایه :
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                    }}
                    noWrap
                  >
                    {addCommas(fare || "")}
                    ریال
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "700",
                    }}
                  >
                    تناژ:
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                    }}
                  >
                    <Typography noWrap variant="body2">
                      {netWeight || "---"} کیلوگرم
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "700",
                    }}
                  >
                    مبدا:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "700",
                    }}
                  >
                    {src}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "700",
                    }}
                  >
                    مقصد:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "700",
                    }}
                  >
                    {dest}
                  </Typography>
                </Box>

                <Box
                  onClick={() => setOpen((prev) => !prev)}
                  sx={{
                    cursor: "pointer",
                    my: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "end",
                    gap: "4px",
                  }}
                >
                  <Typography color={grey[800]}>جزییات</Typography>
                  <Box
                    sx={{
                      transform: open ? "rotate(-90deg)" : "",
                      transition: "300ms",
                    }}
                  >
                    <ArrowTickLeft size={16} color={grey[800]} />
                  </Box>
                </Box>

                {/* <Box
                sx={{
                  display: "flex",
                }}
              >
                <Box
                  sx={{
                    width: "fit-content",
                    borderColor: "info.main",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    backgroundColor: "background.default",
                    padding: "2px",
                    borderRadius: "6px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                    }}
                    noWrap
                  >
                    {addCommas(orderInfo?.goodsCost!)}
                    ریال
                  </Typography>
                </Box>
                <Box
                  sx={{
                    borderColor: "info.main",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    width: "fit-content",
                    backgroundColor: "background.default",
                    padding: "2px",
                    borderRadius: "6px",
                  }}
                >
                  <Typography noWrap variant="body2">
                    {orderInfo?.truckWeightTo} تن
                  </Typography>
                </Box>
              </Box> */}
              </Box>
              {/* <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "700",
                }}
              >
                {goodsName}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" sx={{}}>
                  وضعیت:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "success.main",
                  }}
                >
                  بارنامه صادرشده
                </Typography>
              </Box>
            </Box> */}
            </Box>
            {/* <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexWrap: "wrap",
              gap: "4px",
              justifyContent: "end",
            }}
          >
            <Box
              sx={{
                borderColor: "info.main",
                borderWidth: "1px",
                borderStyle: "solid",
                backgroundColor: "background.default",
                padding: "2px",
                borderRadius: "6px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                }}
                noWrap
              >
                {addCommas(orderInfo?.goodsCost!)}
                ریال
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: "4px",
                justifyContent: "end",
              }}
            >
              <Box
                sx={{
                  borderColor: "info.main",
                  borderWidth: "1px",
                  borderStyle: "solid",
                  backgroundColor: "background.default",
                  padding: "2px",
                  borderRadius: "6px",
                }}
              >
                <Typography noWrap variant="body2">
                  {orderInfo?.truckWeightTo} تن
                </Typography>
              </Box>
              <Box
                sx={{
                  borderColor: "info.main",
                  borderWidth: "1px",
                  borderStyle: "solid",
                  backgroundColor: "background.default",
                  padding: "2px",
                  borderRadius: "6px",
                }}
              >
                <Typography noWrap variant="body2">
                  {orderInfo?.packageTypeInfo.name!}
                </Typography>
              </Box>
            </Box>
          </Box> */}
          </Box>

          <Box
            sx={{
              display: "flex",
              mt: 1,
              gap: "8px",
            }}
          >
            {/* <CustomButton
              onClick={() => setPaymentBottomSheet(true)}
              flex={1}
              justifyContent="center"
              title="پرداخت کرایه"
              bgColor="secondary.main"
              fSize="16px"
              px="6px"
              py="8px"
              borderRadius="6px"
              icon1={<StarFillSVG size={22} color={"#fff"} />}
            /> */}
            <CustomButton
              onClick={handlePrint}
              flex={1}
              justifyContent="center"
              title="چاپ"
              bgColor="success.main"
              fSize="16px"
              px="6px"
              py="8px"
              borderRadius="6px"
              // icon2={<ArrowTickLeft size={18} color={"#fff"} />}
            />
          </Box>
          <Box
            sx={{
              overflow: "hidden",
              maxHeight: open ? "1000px" : 0,
              transition: open
                ? "max-height 0.5s ease"
                : "max-height 0.2s ease",
              display: "flex",
              mt: 1,
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <Box
              sx={{
                flex: "1",
                borderRadius: "8px",
                backgroundColor: "bgColor.grey100",
                display: "flex",
                justifyContent: "space-between",
                boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.1) inset",
                padding: "8px 6px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  فرستنده: {senderInfoFullName}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "14px",
                }}
              ></Typography>
            </Box>
            <Box
              sx={{
                flex: "1",
                borderRadius: "8px",
                backgroundColor: "bgColor.grey100",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.1) inset",
                padding: "8px 6px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  آدرس:
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "14px",
                }}
              >
                {senderAddress}
              </Typography>
            </Box>

            <Box
              sx={{
                flex: "1",
                borderRadius: "8px",
                backgroundColor: "bgColor.grey100",
                display: "flex",
                justifyContent: "space-between",
                boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.1) inset",
                padding: "8px 6px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  گیرنده: {receiverInfoFullName}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "14px",
                }}
              ></Typography>
            </Box>
            <Box
              sx={{
                flex: "1",
                borderRadius: "8px",
                backgroundColor: "bgColor.grey100",
                display: "flex",
                flexDirection: "column",

                justifyContent: "space-between",
                boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.1) inset",
                padding: "8px 6px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  آدرس:
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "14px",
                }}
              >
                {receiverAddress}
              </Typography>
            </Box>

            <Box
              sx={{
                flex: "1",
                borderRadius: "8px",
                backgroundColor: "bgColor.grey100",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.1) inset",
                padding: "8px 6px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  راننده:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  {driverFullName}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    flex: "1",
                    fontSize: "14px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  کدملی: {driverInfoNationalCode}
                </Typography>
                <Typography
                  sx={{
                    flex: "1",
                    fontSize: "14px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  تلفن: {driverInfoMobileNr}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  پلاک:
                </Typography>
                <CarTagView
                  alpha={alpha}
                  firstTowDigits={firstTowDigits}
                  lastThiredDigits={lastThiredDigits}
                  licensePlateSerial={licensePlateSerial}
                />
              </Box>
            </Box>

            <Box
              sx={{
                flex: "1",
                borderRadius: "8px",
                backgroundColor: "bgColor.grey100",
                display: "flex",
                justifyContent: "space-between",
                boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.1) inset",
                padding: "8px 6px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  ارزش محموله:
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "14px",
                }}
              >
                {addCommas(goodsCost)} ریال
              </Typography>
            </Box>
          </Box>
        </Box>
      </>
    );
  }
);

export default DoneBolsCard;
